import type { CanvasElement, Column, Content } from 'pdfmake/interfaces';
import { lineSymbolSwatchSvg } from '../../components/line.symbol.render';
import { ColorPaletteBlue } from '../../components/theme.palette';
import { T, Translate } from '../../i18n';
import {
	grantedLegendItems,
	type LegendItem,
	legendItemLabel,
	type LegendSwatch,
} from '../topological.editor.map/map.legend';
import { type PoleAccuracy, poleAccuracyColor } from '../topological.editor.map/map.point/map.point';
import { HEADING_COLOR } from './map.print.theme';

const SWATCH_COL_WIDTH = 18;
const SWATCH_CENTER_Y = 5;
const POINT_RADIUS = 4;
const ROW_FONT_SIZE = 7;

// Line-style swatches render via SVG in a wider column — canvas can't draw the utility-code text labels.
const LINE_STYLE_SWATCH_COL_WIDTH = 34;
const LINE_STYLE_SWATCH_SVG_WIDTH = 46;
const LINE_STYLE_SWATCH_SVG_HEIGHT = 16;

// Layout budget for wrapping/pagination — see `layoutLegend`.
const HEADING_HEIGHT = 16; // bold 9pt heading + its 5pt bottom margin, rounded up
const ROW_HEIGHT = 11; // one row: 7pt text + [0,1,0,1] margin, rounded up
// A lineStyle swatch's SVG is rendered at LINE_STYLE_SWATCH_COL_WIDTH wide, scaled down from its native
// LINE_STYLE_SWATCH_SVG_WIDTH/HEIGHT while preserving aspect ratio — so it renders taller than ROW_HEIGHT
// budgets for, and rows containing one need their own, taller row height or they overflow their column.
const LINE_STYLE_SWATCH_RENDER_HEIGHT = Math.ceil(
	(LINE_STYLE_SWATCH_COL_WIDTH / LINE_STYLE_SWATCH_SVG_WIDTH) * LINE_STYLE_SWATCH_SVG_HEIGHT
);
const LINE_STYLE_ROW_HEIGHT = LINE_STYLE_SWATCH_RENDER_HEIGHT + 2; // + its [0,1,0,1] margin, rounded up

const ASSET_TYPE_SWATCH_RENDER_SIZE = ROW_HEIGHT;
// Empirical nudge to visually align the icon with the label text's cap-height, not its full row box —
// see the `margin` comment at its use below. Negative: the icon already sits low relative to the text
// without any nudge, so this pulls it up rather than pushing it down further.
const ASSET_TYPE_SWATCH_TOP_NUDGE = -1.5;

const COLUMN_GAP = 16;
const MIN_COLUMN_WIDTH = 110; // narrowest a swatch+label column can get before it needs its own page

// `dot` is the MidSpan capture swatch (UFO-1082). A kind missing from here is silently dropped by
// `groupItems`, so the printed map would draw the dots with nothing in the legend explaining them.
const KIND_ORDER: LegendSwatch['kind'][] = ['line', 'lineStyle', 'point', 'dot', 'assetType', 'collectionLabel'];

interface LegendGroup {
	kind: LegendSwatch['kind'];
	items: LegendItem[];
}

interface LegendColumn {
	kind: LegendSwatch['kind'];
	items: LegendItem[];
}

export interface LegendArea {
	/** Already inset by the panel's own padding — the usable width/height for legend content. */
	width: number;
	height: number;
}

/** A continuation page's legend content. Its width always spans the full `continuationCap` width (like
 *  the first page); only its height shrinks to fit — a page with only a little overflow gets a short
 *  box, not the full cap height. */
export interface LegendContinuationPage {
	content: Content;
	width: number;
	height: number;
}

export interface LegendLayout {
	/** The first page's content, in its caller-given `area`; `null` when there's nothing to show. */
	firstPage: Content | null;
	/** Any overflow, each spanning `continuationCap`'s width but only as tall as it needs to be. */
	continuationPages: LegendContinuationPage[];
}

/**
 * Lay out the full legend (fixed items + per-form line-type items). If every column fits within
 * `area`'s bounds (wrapping into more columns, within the same page, as needed), it all stays on the
 * first page. Otherwise, rather than splitting the legend across the first page and continuation
 * pages, the whole legend moves together onto one or more continuation pages, each as wide as
 * `continuationCap` (which has no map/note to share space with, so it can hold more columns per row
 * than the first page) but only as tall as its own content needs.
 */
export function layoutLegend(extraItems: LegendItem[], area: LegendArea, continuationCap: LegendArea): LegendLayout {
	const groups = groupItems(visibleItems(extraItems));
	if (groups.length === 0) {
		return { firstPage: null, continuationPages: [] };
	}

	const firstPageRowsFor = (kind: LegendSwatch['kind']) => maxRows(area.height, kind);
	const firstPageColumns = chunkGroups(groups, firstPageRowsFor);
	const firstPageFit = Math.max(1, maxFittingCount(firstPageColumns.length, area.width));

	if (firstPageFit >= firstPageColumns.length) {
		return { firstPage: renderPage(firstPageColumns, area.width), continuationPages: [] };
	}

	const continuationRowsFor = (kind: LegendSwatch['kind']) => maxRows(continuationCap.height, kind);
	let remaining = chunkGroups(groups, continuationRowsFor);
	const continuationPages: LegendContinuationPage[] = [];

	while (remaining.length > 0) {
		const count = Math.max(1, maxFittingCount(remaining.length, continuationCap.width));
		continuationPages.push(renderContinuationPage(remaining.slice(0, count), continuationCap.width));
		remaining = remaining.slice(count);
	}

	return { firstPage: null, continuationPages };
}

// The export captures a static view with no selection, so drop the "selected collection" swatch
// and the device/photo-location swatch (its overlay only renders while a capture is selected).
function visibleItems(extraItems: LegendItem[]): LegendItem[] {
	return grantedLegendItems(extraItems).filter(
		(item) => !(item.swatch.kind === 'point' && item.swatch.selected) && item.swatch.kind !== 'device'
	);
}

function groupItems(items: LegendItem[]): LegendGroup[] {
	return KIND_ORDER.map((kind) => ({ kind, items: items.filter((item) => item.swatch.kind === kind) })).filter(
		(group) => group.items.length > 0
	);
}

/** A row's height depends on its swatch — a lineStyle SVG renders taller than the plain canvas/chip
 *  swatches every other kind uses. */
function rowHeightFor(kind: LegendSwatch['kind']): number {
	return kind === 'lineStyle' ? LINE_STYLE_ROW_HEIGHT : ROW_HEIGHT;
}

/** How many rows of the given kind fit in a column of this height, heading included. */
function maxRows(height: number, kind: LegendSwatch['kind']): number {
	return Math.max(1, Math.floor((height - HEADING_HEIGHT) / rowHeightFor(kind)));
}

/** Split each group into columns sized to that group's own row budget, preserving group order. */
function chunkGroups(groups: LegendGroup[], rowsPerColumnFor: (kind: LegendSwatch['kind']) => number): LegendColumn[] {
	const columns: LegendColumn[] = [];
	for (const group of groups) {
		const rowsPerColumn = rowsPerColumnFor(group.kind);
		for (let i = 0; i < group.items.length; i += rowsPerColumn) {
			columns.push({ kind: group.kind, items: group.items.slice(i, i + rowsPerColumn) });
		}
	}
	return columns;
}

/** How many of the first N columns fit, side by side, at a legible width within `pageWidth`. */
function maxFittingCount(totalColumns: number, pageWidth: number): number {
	let best = 0;
	for (let count = 1; count <= totalColumns; count++) {
		const gapTotal = COLUMN_GAP * (count - 1);
		const perColumnWidth = (pageWidth - gapTotal) / count;
		if (perColumnWidth < MIN_COLUMN_WIDTH) {
			break; // narrower with every additional column, so it only gets worse from here
		}
		best = count;
	}
	return best;
}

function renderPage(columns: LegendColumn[], pageWidth: number): Content {
	const gapTotal = COLUMN_GAP * Math.max(0, columns.length - 1);
	const columnWidth = (pageWidth - gapTotal) / Math.max(1, columns.length);

	const pdfColumns: Column[] = columns.map((column) => ({
		width: columnWidth,
		stack: column.items.map((item) => ({ columns: swatchAndLabel(item), margin: [0, 1, 0, 1] })),
	}));

	return { stack: [heading(), { columns: pdfColumns, columnGap: COLUMN_GAP }] };
}

/** Like `renderPage` (columns stretch to fill `pageWidth`, same as the first page), but also reports
 *  the height actually used so the caller can draw a box that's only as tall as this page's content. */
function renderContinuationPage(columns: LegendColumn[], pageWidth: number): LegendContinuationPage {
	const tallestColumn = Math.max(...columns.map((column) => column.items.length * rowHeightFor(column.kind)));
	const height = HEADING_HEIGHT + tallestColumn;

	return { content: renderPage(columns, pageWidth), width: pageWidth, height };
}

function heading(): Content {
	const text = Translate(T.TOPO_PRINT_INCLUDES_LEGEND).toUpperCase();
	return { text, bold: true, fontSize: 9, color: HEADING_COLOR, margin: [0, 0, 0, 5] };
}

function swatchAndLabel(item: LegendItem): Column[] {
	return [
		swatchColumn(item.swatch),
		{ width: '*', text: legendItemLabel(item), fontSize: ROW_FONT_SIZE, margin: [4, 0, 0, 0] },
	];
}

/** The swatch column to the left of each label. Span-line/point swatches are vector canvases; the
 *  collection label is a filled chip and line-style swatches carry text/glyphs, which `canvas` can't
 *  render — so those use a table cell and an SVG respectively. */
function swatchColumn(swatch: LegendSwatch): Column {
	if (swatch.kind === 'collectionLabel') {
		return collectionLabelChip();
	}
	if (swatch.kind === 'device') {
		// Filtered out by visibleItems(); an empty swatch keeps the switch below exhaustive.
		return { width: SWATCH_COL_WIDTH, canvas: [] };
	}
	if (swatch.kind === 'lineStyle') {
		return {
			width: LINE_STYLE_SWATCH_COL_WIDTH,
			svg: lineSymbolSwatchSvg(swatch.symbol, LINE_STYLE_SWATCH_SVG_WIDTH, LINE_STYLE_SWATCH_SVG_HEIGHT),
		};
	}
	if (swatch.kind === 'assetType') {
		// The real glyph, server-rendered to SVG (canvas can't draw the Material Icon font directly).
		// Falls back to a plain colored dot if the fetch failed (e.g. offline export).
		if (swatch.svg != null) {
			// pdfMake's `columns` has no vertical-align, so mixed content top-aligns within the row — the
			// SVG has no internal leading the way the label's text does, so it renders flush at the row's
			// top edge while the text's visible glyph ink starts a bit lower. Nudge the icon down to match.
			return { width: ASSET_TYPE_SWATCH_RENDER_SIZE, svg: swatch.svg, margin: [0, ASSET_TYPE_SWATCH_TOP_NUDGE, 0, 0] };
		}
		return { width: SWATCH_COL_WIDTH, canvas: assetTypeSwatch(swatch.color) };
	}
	return { width: SWATCH_COL_WIDTH, canvas: swatchCanvas(swatch) };
}

/** A blue chip with centered white "abc", mirroring the on-screen collection-label swatch. */
function collectionLabelChip(): Column {
	return {
		width: SWATCH_COL_WIDTH,
		table: {
			widths: ['*'],
			body: [
				[
					{
						text: 'abc',
						color: '#ffffff',
						fontSize: 6,
						alignment: 'center',
						fillColor: rgbToHex(ColorPaletteBlue.C900),
					},
				],
			],
		},
		layout: {
			defaultBorder: false,
			paddingLeft: () => 1,
			paddingRight: () => 1,
			paddingTop: () => 1,
			paddingBottom: () => 1,
		},
	};
}

function swatchCanvas(swatch: Extract<LegendSwatch, { kind: 'line' | 'point' | 'dot' }>): CanvasElement[] {
	switch (swatch.kind) {
		case 'line':
			return [
				{
					type: 'line',
					x1: 0,
					y1: SWATCH_CENTER_Y,
					x2: SWATCH_COL_WIDTH - 2,
					y2: SWATCH_CENTER_Y,
					lineWidth: 3,
					lineColor: rgbToHex(swatch.color),
				},
			];
		case 'point':
			return pointSwatch(swatch.accuracy, swatch.selected);
		case 'dot':
			return dotSwatch(swatch.color);
	}
}

/** A plain filled dot with the same dark rim the on-screen GenericMapPoint draws (MidSpan captures). */
function dotSwatch(color: string): CanvasElement[] {
	return [
		{
			type: 'ellipse',
			x: POINT_RADIUS + 1,
			y: SWATCH_CENTER_Y,
			r1: POINT_RADIUS,
			r2: POINT_RADIUS,
			color,
			lineColor: '#000000',
			lineWidth: 1,
		},
	];
}

function assetTypeSwatch(color: string): CanvasElement[] {
	const cx = POINT_RADIUS + 1;
	return [
		{
			type: 'ellipse',
			x: cx,
			y: SWATCH_CENTER_Y,
			r1: POINT_RADIUS,
			r2: POINT_RADIUS,
			color: rgbToHex(color),
			lineColor: '#000000',
			lineWidth: 0.5,
		},
	];
}

function pointSwatch(accuracy: PoleAccuracy, selected?: boolean): CanvasElement[] {
	const cx = POINT_RADIUS + 1;
	const elements: CanvasElement[] = [];

	// White halo behind the selected marker, echoing the on-screen selected ring.
	if (selected) {
		elements.push({
			type: 'ellipse',
			x: cx,
			y: SWATCH_CENTER_Y,
			r1: POINT_RADIUS + 1.5,
			r2: POINT_RADIUS + 1.5,
			color: '#ffffff',
		});
	}

	elements.push({
		type: 'ellipse',
		x: cx,
		y: SWATCH_CENTER_Y,
		r1: POINT_RADIUS,
		r2: POINT_RADIUS,
		color: rgbToHex(poleAccuracyColor[accuracy]),
		lineColor: '#000000',
		lineWidth: 0.5,
	});

	// The "reviewed" marker carries a small black center dot on the map; mirror it here.
	if (accuracy === 'edited') {
		elements.push({ type: 'ellipse', x: cx, y: SWATCH_CENTER_Y, r1: 1, r2: 1, color: '#000000' });
	}

	return elements;
}

/** Convert an `"r,g,b"` palette value or a `rgb()/rgba()` CSS string to a `#`-prefixed hex pdfMake understands. */
function rgbToHex(value: string): string {
	if (value.startsWith('#')) {
		return value;
	}
	const parts = value.match(/\d+/g);
	if (!parts || parts.length < 3) {
		return '#000000';
	}
	return (
		'#' +
		parts
			.slice(0, 3)
			.map((n) => Number(n).toString(16).padStart(2, '0'))
			.join('')
	);
}
